import React, { useState } from "react";
import useSWR from "swr";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, XCircle, MessageSquare, Send, Search, RefreshCcw, Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const KIND_LABEL = {
  reminder_h7: "Reminder H-7",
  reminder_due: "Reminder Jatuh Tempo",
  receipt: "Kwitansi",
};

function formatWhen(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function WaLogs() {
  const [filters, setFilters] = useState({ kind: "", status: "", tenant: "" });
  const [running, setRunning] = useState(false);
  const [retrying, setRetrying] = useState(null); // log id currently retrying
  const qs = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => v && qs.append(k, v));
  qs.append("limit", "200");
  const { data, isLoading, mutate: reload } = useSWR(`/reminders/logs?${qs.toString()}`, fetcher);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/reminders/run-now");
      if (data?.skipped) {
        toast.warning(data.reason === "no_token" ? "Token Fonnte belum diatur di .env" : "Scheduler dilewati");
      } else {
        const h7 = data?.h7 || {}, due = data?.due || {};
        toast.success(`Selesai · H-7: sent ${h7.sent||0}/failed ${h7.failed||0} · Jatuh tempo: sent ${due.sent||0}/failed ${due.failed||0}`);
      }
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal jalankan scheduler");
    } finally {
      setRunning(false);
    }
  };

  const retry = async (log) => {
    setRetrying(log.id);
    try {
      const { data } = await api.post(`/reminders/retry/${log.id}`);
      toast.success(`Berhasil terkirim ke ${data.target}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal kirim ulang");
    } finally {
      setRetrying(null);
    }
  };

  const items = data?.items || [];
  const summary = data?.summary || { total: 0, ok: 0, failed: 0 };

  return (
    <div className="space-y-6" data-testid="wa-logs-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Integrasi Fonnte</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Log WhatsApp</h1>
          <p className="text-sm text-muted-foreground mt-1">Riwayat semua reminder & kwitansi yang terkirim (atau gagal) via Fonnte.</p>
        </div>
        <Button onClick={runNow} disabled={running} className="gap-2 shrink-0" data-testid="wa-run-now">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {running ? "Menjalankan..." : "Jalankan Scheduler Sekarang"}
        </Button>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Total" value={summary.total} tone="primary" testid="wa-stat-total" />
        <StatCard label="Terkirim" value={summary.ok} tone="success" testid="wa-stat-ok" />
        <StatCard label="Gagal" value={summary.failed} tone="destructive" testid="wa-stat-failed" />
      </div>

      <Card>
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Jenis</label>
            <Select value={filters.kind || "all"} onValueChange={(v) => setFilters({ ...filters, kind: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="wa-filter-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua jenis</SelectItem>
                <SelectItem value="reminder_h7">Reminder H-7</SelectItem>
                <SelectItem value="reminder_due">Reminder Jatuh Tempo</SelectItem>
                <SelectItem value="receipt">Kwitansi</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Status</label>
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="wa-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="ok">Terkirim</SelectItem>
                <SelectItem value="failed">Gagal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Cari penyewa</label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Ketik nama penyewa..."
                className="pl-9"
                value={filters.tenant}
                onChange={(e) => setFilters({ ...filters, tenant: e.target.value })}
                data-testid="wa-filter-tenant"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Memuat...</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center flex flex-col items-center gap-2">
            <MessageSquare className="h-8 w-8 text-muted-foreground" />
            <div className="font-medium">Belum ada log WhatsApp</div>
            <p className="text-xs text-muted-foreground">Kirim reminder/kwitansi dari halaman Tagihan atau Kwitansi.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <Card key={it.id || i} className={it.ok ? "" : "border-destructive/40"} data-testid={`wa-log-${i}`}>
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`shrink-0 h-9 w-9 rounded-md grid place-items-center ${it.ok ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
                  {it.ok ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="rounded-full text-[10px] uppercase tracking-wider">
                      {KIND_LABEL[it.kind] || it.kind}
                    </Badge>
                    {it.meta?.auto && (
                      <Badge variant="secondary" className="rounded-full text-[10px] uppercase tracking-wider">Auto</Badge>
                    )}
                    {it.meta?.bulk && (
                      <Badge variant="secondary" className="rounded-full text-[10px] uppercase tracking-wider">Massal</Badge>
                    )}
                    {it.meta?.retry && (
                      <Badge variant="secondary" className="rounded-full text-[10px] uppercase tracking-wider">Retry</Badge>
                    )}
                    <span className="text-xs text-muted-foreground">{formatWhen(it.created_at)}</span>
                  </div>
                  <div className="mt-1 text-sm font-medium">{it.tenant_name || "—"}</div>
                  <div className="text-xs text-muted-foreground font-mono">{it.target || "(nomor kosong)"}</div>
                  {!it.ok && (
                    <div className="text-xs text-destructive mt-1 italic break-words">Error: {it.error || "unknown"}</div>
                  )}
                </div>
                {!it.ok && it.id && it.ref_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 shrink-0"
                    onClick={() => retry(it)}
                    disabled={retrying === it.id}
                    data-testid={`wa-retry-${i}`}
                  >
                    {retrying === it.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}
                    Coba Lagi
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const cls =
    tone === "success" ? "bg-success/10 text-success" :
    tone === "destructive" ? "bg-destructive/10 text-destructive" :
    "bg-primary/10 text-primary";
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
        <div className="flex items-end justify-between mt-2">
          <div className="font-display text-3xl font-medium tracking-tighter tabular-nums">{value}</div>
          <div className={`h-8 w-8 rounded-md grid place-items-center ${cls}`}>
            <MessageSquare className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
